import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { getEventDetails } from '@store/slices/eventSlice';
import { useCallback, useMemo } from 'react';
import { MenuItem as MenuItemType } from '@hubs101/booking-api-skd-client/lib/types';
import {
  ButtonsWrapper,
  DesktopMenuWrapper,
  HeaderWrapper,
  LogoImage,
  LogoWrapper,
  ContentWrapper,
} from './styles';
import { getImageSrc } from '../../../../utils/fn';
import weLogo from '@assets/images/weCONECT_col_RGB.svg';
import Button from '@components/Button';
import { useNavigate } from 'react-router-dom';
import { EventPosterSectionMenuItems } from '../EventPosterSectionMenuItems';
import EventCounter from '../EventCounter';
import { usePublicCoLocatedEventsByEventId } from '@utils/apiHooks';
import { getMailToLink } from '../EventPosterSectionMenu';
import { useExtraLargeDesktopWidth } from '@utils/hooks';

export const mappingLinks = {
  home: '',
  agenda: 'agenda-url',
  speaker: 'speakers-url',
  partner: 'partners-url',
  'who-why': 'who-&-why-url',
  'content-hub': 'content-hub-url',
  faq: 'faq-url',
};

export const ScrollableEventPosterSectionMenu = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const navigate = useNavigate();
  const smallerThanExtraLargeDesktop = useExtraLargeDesktopWidth();

  const { data: coLocatedEvents, isLoading: isCoLocatedEventsLoading } =
    usePublicCoLocatedEventsByEventId({
      eventId: sluggedEvent?.id,
      enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
    });

  const sortedMenuItems = useMemo(
    () =>
      sluggedEvent?.website_menu
        ?.find(e => e.type === 'mainmenu')
        ?.items?.toSorted(
          (a: MenuItemType, b: MenuItemType) => a.order - b.order
        ),
    [sluggedEvent?.website_menu]
  );

  const link = useCallback(
    (item: MenuItemType) => {
      switch (item.type) {
        case 'internal':
          return `/${String(item?.url ?? '')}`;
        case 'external':
          return item?.url ?? '';
        case 'mailto':
          return getMailToLink({
            sluggedEvent: sluggedEvent,
            coLocatedEvents: coLocatedEvents,
            email: item?.to ?? '',
            subject: item?.subject ?? '',
            body: item?.body ?? '',
          });
        default:
          return t(
            mappingLinks[item.type as keyof typeof mappingLinks] ??
              `/${String(item.url)}`
          );
      }
    },
    [t]
  );

  const mainMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => !e.level)
        ?.map(item => {
          return {
            id: item.label,
            title: item.label,
            subtitle: item.subtitle,
            link: link(item),
            order: item.order,
          };
        }),
    [link, sortedMenuItems]
  );

  const secondaryMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => e.level)
        ?.map((item: MenuItemType) => {
          return {
            id: item.label,
            title: item.label,
            subtitle: item.subtitle,
            link: link(item),
            order: item.order,
            index: mainMenuItems?.findLastIndex(
              element => element.order < item.order
            ),
            isDesktopDisabled: item.isDesktopDisabled,
            isTabletDisabled: item.isTabletDisabled,
            isMobileDisabled: item.isMobileDisabled,
            to: item.to,
            subject: item.subject,
            body: item.body,
            styling: String(item.styling),
            type: item.type,
            element: item?.element,
          };
        }) ?? [],
    [link, mainMenuItems, sortedMenuItems]
  );

  return (
    <DesktopMenuWrapper>
      <HeaderWrapper $themeColor={sluggedEvent?.design?.themeColor}>
        <EventCounter />
      </HeaderWrapper>

      <ContentWrapper>
        <LogoWrapper>
          <LogoImage
            $maxWidth={'15.6rem'}
            src={
              sluggedEvent?.design?.eventLogoPosSvg?.length > 0
                ? getImageSrc(sluggedEvent?.design?.eventLogoPosSvg)
                : weLogo
            }
            alt={
              sluggedEvent?.name?.length ? sluggedEvent?.name : 'WeConect Logo'
            }
            $height={`${4}rem`}
          />
        </LogoWrapper>

        <EventPosterSectionMenuItems
          mainMenuItems={mainMenuItems}
          sluggedEvent={sluggedEvent}
          secondaryMenuItems={secondaryMenuItems}
          themeColor={sluggedEvent?.design?.themeColor}
          from="scrollable"
          coLocatedEvents={coLocatedEvents}
        />

        <ButtonsWrapper>
          <Button
            title={t('exhibit-now')}
            backgroundColor={theme.color.transparent}
            hoverBackgroundColor={sluggedEvent?.design?.themeColor}
            textColor={sluggedEvent?.design?.themeColor}
            hoverTextColor={theme.color.white}
            border={`1px solid ${sluggedEvent?.design?.themeColor}`}
            borderRadius={theme.borderRadius.radius10}
            fontSize={
              smallerThanExtraLargeDesktop
                ? theme.fontSize.size15
                : theme.fontSize.size20
            }
            lineHeight={theme.lineHeight.height100}
            fontWeight="normal"
            padding="0.6rem 3rem"
            width="auto"
            margin="0 1rem 0 0"
            onClick={() => {
              navigate(`/events/${sluggedEvent?.slug}/bp-packages`);
            }}
          />
          <Button
            title={t('book-now')}
            backgroundColor={sluggedEvent?.design?.themeColor}
            hoverBackgroundColor={theme.color.primaryText}
            textColor={theme.color.white}
            hoverTextColor={theme.color.black}
            borderRadius={theme.borderRadius.radius10}
            fontSize={
              smallerThanExtraLargeDesktop
                ? theme.fontSize.size15
                : theme.fontSize.size20
            }
            lineHeight={theme.lineHeight.height150}
            fontWeight="normal"
            padding="0.6rem 3rem"
            border="1px solid transparent"
            width="auto"
            onClick={() => {
              navigate(`/events/${sluggedEvent?.slug}${t('book-now-url')}`);
            }}
          />
        </ButtonsWrapper>
      </ContentWrapper>
    </DesktopMenuWrapper>
  );
};
